// Source of truth for the model + effort selectors.
//
// The model list and per-model effort levels mirror the official Anthropic
// docs (https://platform.claude.com/docs/en/build-with-claude/effort),
// re-verified 2026-07-25 after the Opus 5 / Sonnet 5 launch; Fable 5.1
// added 2026-09-03. Sprint trim-model-selector (2026-09-06, Santi) pruned
// the picker to the 6 models in use — Fable 5.1, Fable 5, Opus 5, Opus 4.8,
// Sonnet 5, Haiku 4.5 — dropping Opus 4.7/4.6/4.5 and Sonnet 4.6/4.5. All 6
// kept models bar Haiku 4.5 support the full low→max effort range (Haiku 4.5
// has no effort control).
//
// Ordering is meaningful: ModelEffortChips renders this array verbatim,
// so the first entry of each family is the default pick for "an Opus" /
// "a Sonnet" (sprint add-opus-5-sonnet-5-models D2).

import type { EffortLevel } from '../types';

export interface ModelOption {
  label: string;
  value: string;
  /** Effort levels this model supports. Empty = no effort control. */
  supportedEfforts: readonly EffortLevel[];
}

// Sprint mll-discriminator (campaign multi-llm W1, spec D5) — provider-keyed
// source of truth. `MODEL_OPTIONS` below is DERIVED (flattened) from this,
// pinned byte-identical to the pre-sprint flat literal by
// `__tests__/models-flat-equality.test.ts`. UI grouping (rendering these as
// separate provider sections in the selector) is W5 scope — today there is
// exactly one group, so the flattened shape is unchanged. Provider id kept
// as a local string-literal type (not imported from the backend's
// `providers/registry.ts`) — this package has no shared-types boundary with
// the backend today, and inventing one for a single literal is scope creep.
export type ModelProviderId = 'claude';

export interface ProviderModelGroup {
  provider: ModelProviderId;
  models: ReadonlyArray<ModelOption>;
}

export const PROVIDER_MODEL_GROUPS: ReadonlyArray<ProviderModelGroup> = [
  {
    provider: 'claude',
    models: [
      // Sprint trim-model-selector (2026-09-06, Santi directive) — the
      // selector had grown to 11 Claude models; trimmed to the 6 Santi
      // actually uses. Removed: Opus 4.7 / 4.6 / 4.5, Sonnet 4.6 / 4.5
      // ("nunca los voy a usar"). These IDs still RUN if an existing agent
      // carries one (nothing rejects them) — they're just no longer offered
      // in the picker, and an old agent on one renders its raw ID per the
      // fix-opus-alias-drift visible-drift doctrine below.
      { label: 'Fable 5.1',  value: 'claude-fable-5-1',           supportedEfforts: ['low', 'medium', 'high', 'xhigh', 'max'] },
      { label: 'Fable 5',    value: 'claude-fable-5',             supportedEfforts: ['low', 'medium', 'high', 'xhigh', 'max'] },
      { label: 'Opus 5',     value: 'claude-opus-5',              supportedEfforts: ['low', 'medium', 'high', 'xhigh', 'max'] },
      { label: 'Opus 4.8',   value: 'claude-opus-4-8',            supportedEfforts: ['low', 'medium', 'high', 'xhigh', 'max'] },
      { label: 'Sonnet 5',   value: 'claude-sonnet-5',            supportedEfforts: ['low', 'medium', 'high', 'xhigh', 'max'] },
      { label: 'Haiku 4.5',  value: 'claude-haiku-4-5-20251001',  supportedEfforts: [] },
    ],
  },
];

export const MODEL_OPTIONS: ReadonlyArray<ModelOption> = PROVIDER_MODEL_GROUPS.flatMap(
  (group) => group.models,
);

// Sprint default-model-fable-5-1 (2026-09-03) — Fable 5.1 at effort=high
// replaces Fable 5 as the fleet-wide default for every new agent on every
// runner and every project. Fable 5.1 (GA 2026-09-01) is ~25% cheaper than
// Fable 5 for typical workloads and stronger at coding, same 1M context and
// same API surface; Claude Code defaults it to High. Continues the fleet
// default set by Santi's 2026-06-09 directive ("por defecto, Fable 5 High
// en todos los runners y en todos los proyectos"), bumped to the newest
// Fable per revealed preference (Santi 2026-09-03: "salió Fable 5.1, mira a
// ver si lo podemos implementar").
//
// RUNTIME GATE — verified 2026-09-06 (sprint mll-discriminator, campaign
// multi-llm TRD §4 D10). All 3 signals confirmed on runner-6 (CLI 2.1.260):
// stream-json `init.model`, assistant `message.model`, and
// `result.modelUsage` all reported `claude-fable-5-1` with no silent
// fallback — `bash scripts/verify-model-on-runner.sh claude
// claude-fable-5-1`, full transcript in
// `docs/sprints/sprint-mll-discriminator-results.md`. This completes the
// `sprint-default-model-fable-5-1-results.md` §5 run, which evidenced only
// `modelUsage` (1 of 3 signals) on runner-8. Re-verify with the same script
// before any future CLI floor bump or model default flip — see
// `docs/runbooks/upgrade-claude-cli.md` for the procedure.
// All models + efforts remain user-selectable via the chip; this constant
// only sets the create-agent default.
export const DEFAULT_MODEL: string = 'claude-fable-5-1';
export const DEFAULT_EFFORT: EffortLevel = 'high';

// Sprint fix-opus-alias-drift (2026-06-03) — removed the ALIAS_MAP
// that translated CLI aliases ('opus'/'sonnet'/'haiku'/'best') into
// canonical IDs at render time. The map's `opus → claude-opus-4-8`
// disagreed with the Claude CLI's own alias resolution
// (`opus → claude-opus-4-7` on CLI 2.1.152), so any agent stored with
// `model: 'opus'` rendered as "Opus 4.8" in the chip but ran as 4.7.
// The fix is structural: the dashboard only stores canonical IDs
// end-to-end. Legacy rows with model='opus' surface the literal string
// in the chip until the one-off migration in
// `backend/src/db/migrations/037_canonicalize_model_aliases.sql`
// rewrites them — by design, so the lie is visible instead of papered over.

export interface EffortOption {
  label: string;
  value: EffortLevel;
}

export const EFFORT_OPTIONS: ReadonlyArray<EffortOption> = [
  { label: 'Low',        value: 'low' },
  { label: 'Medium',     value: 'medium' },
  { label: 'High',       value: 'high' },
  { label: 'Extra-high', value: 'xhigh' },
  { label: 'Max',        value: 'max' },
];

/** Look up the ModelOption for a canonical model ID. */
function findModel(model: string): ModelOption | undefined {
  return MODEL_OPTIONS.find((m) => m.value === model);
}

/**
 * Human label for a canonical model ID. Exact match wins; otherwise a
 * prefix match tolerates dated IDs (e.g. `claude-sonnet-4-5-20250929`
 * → "Sonnet 4.5"). Per fix-opus-alias-drift doctrine (above) an unknown
 * ID renders raw so drift stays visible instead of being papered over.
 */
export function modelLabel(modelId: string): string {
  const exact = findModel(modelId);
  if (exact) return exact.label;
  const prefix = MODEL_OPTIONS.find((m) => modelId.startsWith(m.value));
  return prefix ? prefix.label : modelId;
}

/** Effort options available for the given model. */
export function effortsForModel(model: string): EffortOption[] {
  const opt = findModel(model);
  const supported = opt?.supportedEfforts ?? [];
  if (supported.length === 0) return [];
  return EFFORT_OPTIONS.filter((e) => supported.includes(e.value));
}

/** True if the given model supports effort at all. */
export function modelSupportsEffort(model: string): boolean {
  return effortsForModel(model).length > 0;
}

/** Default effort for a given model per Anthropic docs. */
export function defaultEffortForModel(model: string): EffortLevel {
  const opt = findModel(model);
  if (!opt || opt.supportedEfforts.length === 0) return DEFAULT_EFFORT;
  if (opt.value === 'claude-fable-5-1') return 'high';
  if (opt.value === 'claude-fable-5') return 'high';
  // Opus 5 + Sonnet 5: Anthropic recommends starting at high (unlike
  // 4.7/4.8 where xhigh was the recommended coding starting point).
  if (opt.value === 'claude-opus-5') return 'high';
  if (opt.value === 'claude-sonnet-5') return 'high';
  if (opt.value === 'claude-opus-4-8') return 'high';
  return 'high';
}

/**
 * If the currently selected effort is no longer available on the new model,
 * fall back to that model's default; otherwise keep the existing choice.
 */
export function reconcileEffort(
  model: string,
  current: EffortLevel,
): EffortLevel {
  const available = effortsForModel(model);
  if (available.length === 0) return defaultEffortForModel(model);
  return available.some((e) => e.value === current)
    ? current
    : defaultEffortForModel(model);
}

// ---------- Prompt-length thresholds (D1) ----------

/**
 * Char-count thresholds for the prompt textarea. We use raw character counts
 * as a coarse proxy for token count (real tokenization varies). The numbers
 * align with the Sprint 5 spec.
 */
export const PROMPT_WARN_CHARS = 100_000;
export const PROMPT_MAX_CHARS = 150_000;
